import { IPDefaultButton, IPIconButton, IPModal, IPPrimaryButton, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import InfoMessage from 'components/Notification/InfoMessage';
import SuccesMessage from 'components/Notification/SuccesMessage';
import { globalStackTokensGapSmall } from 'globalStyles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrokeredDirectoryExplorer } from './BrokeredDirectoryExplorer';

interface IBrokeredFolderPickerProps {
  isOpen: boolean;
  connectionId: number;
  // A folder inside a category: the explorer starts in the category's container and root and stays below them.
  containerId?: string;
  rootFolderId?: string;
  close: () => void;
  onSelect: (containerId: string, folderId: string | undefined, path: string) => void;
}

interface IPickedLocation {
  containerId: string;
  folderId: string | undefined;
  path: string;
  /** False on a container that is a browsing device rather than a place; Select waits for a folder inside it. */
  canBeRoot: boolean;
}

/**
 * Picks the place at a document provider that a library category will live in, in the same
 * shape as SharepointFolderPicker: a full height modal holding the explorer, with the choice
 * and the Select button in a footer.
 *
 * Select takes the folder currently being shown, not a highlighted row. A provider folder is
 * opened by name and its children are what tells you it is the right one, so "where I am" is
 * the thing being chosen; the footer spells that place out so it is never a guess.
 */
const BrokeredFolderPicker = (props: IBrokeredFolderPickerProps) => {
  const { t } = useTranslation(['documentproviders', 'translation']);
  const [location, setLocation] = useState<IPickedLocation | undefined>(undefined);

  const renderLocation = () => {
    if (!location) {
      return <InfoMessage textSize="medium" message={t('documentproviders:Picker.NoFolderSelected')} />;
    }

    if (!location.canBeRoot) {
      return <InfoMessage textSize="medium" message={t('documentproviders:Picker.ContainerNotSelectable')} />;
    }

    return <SuccesMessage message={t('documentproviders:Picker.SelectedLocation', { path: location.path })} />;
  };

  //
  // Main render
  //
  return (
    <IPModal isOpen={props.isOpen} onDismiss={props.close} isBlocking>
      <IPStack
        verticalFill
        styles={{ root: { height: '80vh', width: 1000, minWidth: 320, maxWidth: '90vw', padding: 20 } }}
      >
        <IPStack.Item>
          <IPStack horizontal styles={{ root: { justifyContent: 'space-between' } }}>
            <IPStack.Item>
              <IPText variant="xLarge">{t('documentproviders:Picker.SelectFolder')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPIconButton iconProps={{ iconName: 'CalculatorMultiply' }} onClick={() => props.close()} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item grow>
          <BrokeredDirectoryExplorer
            connectionId={props.connectionId}
            containerId={props.containerId}
            rootFolderId={props.rootFolderId}
            selectFolderMode
            onNavigate={(containerId, folderId, _breadcrumb, path, canBeRoot) => {
              setLocation(containerId ? { containerId, folderId, path, canBeRoot } : undefined);
            }}
          />
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item>
          <IPStack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
              {renderLocation()}
            </IPStack.Item>
            <IPStack.Item>
              <IPStack horizontal tokens={globalStackTokensGapSmall}>
                <IPPrimaryButton
                  text={t('translation:General.Button.Select')}
                  disabled={!location?.canBeRoot}
                  onClick={() => {
                    if (location?.canBeRoot) {
                      props.onSelect(location.containerId, location.folderId, location.path);
                      props.close();
                    }
                  }}
                />
                <IPDefaultButton text={t('translation:General.Button.Cancel')} onClick={() => props.close()} />
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </IPModal>
  );
};

export default BrokeredFolderPicker;
